import { Grid } from '@material-ui/core';
import copy from 'copy-to-clipboard';
import MaterialTable, { Column } from 'material-table';
import moment from 'moment';
import React from 'react';
import ReactJson, { OnCopyProps } from 'react-json-view';

import { IEndpointRequestLiveLogs } from '~/src/dash/api/live_logs/models';
import { CopyButton } from '~/src/dash/components/button';
import { IStore } from '~/src/dash/reducers/store';
import {
  IBaseState,
  IHistoryProps,
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
import { decorate } from '~/src/dash/utils/decorator';
import { utils } from '~/src/dash/utils/json';

interface IRowEndpointsRequestLiveLogs {
  timestamp: string;
  publisherName: string;
  endpointURI: string;
  // auctionRequester: string;
  endpointBody: string;
  httpHeaders: Record<string, string>;
  originalEndpointURI: string;
}

export interface IProps
  extends IWithStylesProps,
    IReduxActionProps,
    ISessionStateProps,
    IUserStateProps,
    IHistoryProps {
  logs: IEndpointRequestLiveLogs[];
}

interface IState extends IBaseState {
  tableColumns: Array<Column<IRowEndpointsRequestLiveLogs>>;
  tableData: IRowEndpointsRequestLiveLogs[];
}

type Globals = '-moz-initial' | 'inherit' | 'initial' | 'revert' | 'unset';
type WhiteSpaceProperty =
  | Globals
  | '-moz-pre-wrap'
  | 'break-spaces'
  | 'normal'
  | 'nowrap'
  | 'pre'
  | 'pre-line'
  | 'pre-wrap';

function dataToRow(
  data: IEndpointRequestLiveLogs[],
  publisherTimezone: string,
): IRowEndpointsRequestLiveLogs[] {
  if (!Array.isArray(data) || data === undefined) {
    return [];
  }
  const mappedData = (data || [])
    .map((d: IEndpointRequestLiveLogs): IRowEndpointsRequestLiveLogs => {
      return {
        endpointURI: d.endpointURI,
        publisherName: d.publisherUUID,
        timestamp:
          moment(d.timestamp).tz(publisherTimezone).format('DD-MM-YYYY[\n]HH:mm:ss') +
          '\n' +
          publisherTimezone,
        // auctionRequester: d.auctionRequester === '' ? 'Standard' : d.auctionRequester,
        endpointBody: d.endpointBody,
        httpHeaders: d.httpHeaders,
        originalEndpointURI: d.OriginalEndpointURI,
      };
    })
    .sort((a: IRowEndpointsRequestLiveLogs, b: IRowEndpointsRequestLiveLogs): number => {
      // Sort data by timestamp desc
      return (
        moment(b.timestamp, 'DD-MM-YYYY HH:mm:ss').unix() -
        moment(a.timestamp, 'DD-MM-YYYY HH:mm:ss').unix()
      );
    });

  return mappedData;
}

export const LogEndpointRequestTable = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    styles: (theme: any) => ({
      paper: {
        ...theme.mixins.gutters(),
        paddingTop: theme.spacing.unit * 2,
        paddingBottom: theme.spacing.unit * 2,
        marginBottom: theme.spacing.unit * 4,
      },
      section: {
        display: 'block',
        marginBottom: theme.spacing.unit * 4,
      },
      button: {
        margin: theme.spacing.unit,
      },
    }),
    router: true,
  },
  class extends React.Component<IProps, IState> {
    state = {
      tableColumns: [
        {
          title: 'Timestamp',
          field: 'timestamp',
          searchable: false,
          width: '40px',
          cellStyle: { whiteSpace: 'pre-wrap' as WhiteSpaceProperty },
        },
        { title: 'Endpoint URI', field: 'endpointURI', searchable: false },
        // { title: 'Request Format', field: 'auctionRequester', searchable: false },
      ],
      tableData: dataToRow(this.props.logs, this.props.session.publisher.Timezone),
    };

    handleCopy = (copyObj: OnCopyProps) => {
      copy(JSON.stringify(copyObj.src, null, '\t'));
    };

    componentDidUpdate(prevProps: Readonly<IProps>): void {
      if (prevProps.logs !== this.props.logs) {
        this.setState({
          tableData: dataToRow(this.props.logs, this.props.session.publisher.Timezone),
        });
      }
    }

    render() {
      const shouldDisplayRequestBody =
        this.state.tableData.find(
          (row: IRowEndpointsRequestLiveLogs) => row.endpointBody !== '',
        ) !== undefined;

      return (
        <div>
          <MaterialTable
            style={{
              paddingLeft: '20px',
              paddingRight: '20px',
            }}
            title=''
            options={{
              search: false,
              pageSize: 15,
              pageSizeOptions: [5, 15, 30],
              toolbar: false,
            }}
            columns={this.state.tableColumns}
            data={this.state.tableData}
            detailPanel={(rowData: IRowEndpointsRequestLiveLogs) => {
              return (
                <Grid container direction='column'>
                  {/* HTTP headers*/}
                  <Grid item>
                    <Grid container direction='row' justify='flex-start' alignItems='center'>
                      <Grid item></Grid>
                      <Grid item>
                        <p>HTTP Headers</p>
                      </Grid>
                      {/* <Grid item><CopyButton copy={JSON.stringify(rowData.httpHeaders)} text='Copy'/></Grid>*/}
                    </Grid>
                    <ReactJson
                      displayDataTypes={false}
                      src={rowData.httpHeaders}
                      enableClipboard={this.handleCopy}
                    />
                  </Grid>

                  {/* Original Endpoint URI for Inventory Rules */}
                  {rowData.originalEndpointURI !== '' &&
                    rowData.endpointURI !== rowData.originalEndpointURI && (
                      <Grid item>
                        <Grid container direction='row' justify='flex-start' alignItems='center'>
                          <Grid item></Grid>
                          <Grid item>
                            <p>Original Endpoint URI</p>
                          </Grid>
                        </Grid>
                        <ReactJson
                          displayDataTypes={false}
                          src={{
                            originalEndpointURI: rowData.originalEndpointURI,
                          }}
                          enableClipboard={this.handleCopy}
                        />
                      </Grid>
                    )}

                  {/* Response*/}
                  {shouldDisplayRequestBody && (
                    <Grid item>
                      <Grid container direction='row' justify='flex-start' alignItems='center'>
                        <Grid item></Grid>
                        <Grid item>
                          <p>Request Body</p>
                        </Grid>
                        <Grid item>
                          <CopyButton copy={rowData.endpointBody} text='Copy' />
                        </Grid>
                      </Grid>
                      <ReactJson
                        displayDataTypes={false}
                        src={utils.json.SafeParse(rowData.endpointBody)}
                        enableClipboard={this.handleCopy}
                      />
                    </Grid>
                  )}
                </Grid>
              );
            }}
          />
        </div>
      );
    }
  },
);
